import { ChatBubbleLeftRightIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { ActionList, ActionListItem } from '../../components/ActionList';
import { Button } from '../../components/Button';
import { CreditBalance } from '../../components/CreditLedger/CreditBalance';
import { SubscriptionHeader } from '../../components/CreditLedger/SubscriptionHeader';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { PageContent } from '../../components/page/PageContent';
import { PageHeader } from '../../components/page/PageHeader';
import { UserContext } from '../../contexts/UserContext';
import useOpen from '../../hooks/useOpen';
import { BuyCreditsModal } from '../../modules/organisation/BuyCreditsModal';
import { CancelSubscriptionModal } from '../../modules/organisation/CancelSubscriptionModal';
import { PlanPicker } from '../../modules/organisation/PlanPicker';
import { TransactionsModal } from '../../modules/organisation/TransactionsModal';
import { T } from '../../translation/src';
import { Permissions, useOrganisationsQuery } from '../../types/graphqlTypes';
import { ContactSalesModal } from '../../modules/organisation/ContactSalesModal';

export const Subscription: React.FC2 = () => {
  const user = useContext(UserContext);
  const { organisationId = '' } = useParams();
  const { data, loading } = useOrganisationsQuery(user ? { variables: { includeIds: [organisationId] } } : {});
  const organisation = data?.organisations.find((org) => org.id === organisationId);
  const { onClose: closeTransactionsModal, open: transactionsModalOpen, onOpen: openTransactionsModal } = useOpen();
  const { onClose: closeBuyModal, open: buyModalOpen, onOpen: openBuyModal } = useOpen();
  const { onClose: closeContactSalesModal, open: contactSalesModalOpen, onOpen: openContactSalesModal } = useOpen();
  const { onClose: closeCancelModal, open: cancelModalOpen, onOpen: openCancelModal } = useOpen();

  const canManageCredits = user?.isSuperAdmin || organisation?.myPermissions.some((permission) => permission === Permissions.All || permission === Permissions.Organisations);
  // Billed to a partner: the partner orders for the organisation and is invoiced; here the plan is only shown,
  // without prices or invoices, and cancelling is there only if the partner allows it.
  const billedTo = organisation?.referral.billedTo;
  const canOrder = canManageCredits && !billedTo;
  const canCancel = canManageCredits && (!billedTo || !!organisation?.referral.canCancelSubscription);
  // Whether the term can be cancelled (it then stops renewing) is the server's rule; its end date is what the customer keeps until.
  const subscription = organisation?.subscription;
  const cancellableEndDate = subscription?.cancellable && subscription.endDate ? subscription.endDate : undefined;

  return (
    <PageContent>
      <PageHeader title={<T _str="subscription" swc />} />

      {/* The flexible remainder under the header, scrolling on its own: a block, so the column inside sets the scroll height. */}
      <div className="w-full min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <EarthLoader />
        ) : (
          <div className="flex flex-col w-full max-w-4xl space-y-4 pb-10">
            {organisation && (
              <>
                <SubscriptionHeader
                  type={organisation.subscription.type}
                  scheduledType={organisation.subscription.scheduledType}
                  scheduledAt={organisation.subscription.scheduledAt}
                  startDate={organisation.subscription.startDate}
                  endDate={organisation.subscription.endDate}
                  pausedAt={organisation.subscription.pausedAt}
                  monthlyAllowance={organisation.credits.monthlyAllowance}
                />
                {billedTo && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-5 text-sm shadow-sm dark:border-gray-700 dark:bg-[#242424]">
                    <T _str="Your subscription is managed by {partner}. Contact them to change your plan or buy projects." args={{ partner: billedTo.name }} />
                  </div>
                )}
                {canOrder && <PlanPicker organisationId={organisation.id} currentType={organisation.subscription.type} />}
                {/* Buying sits with the number it changes — it is the one primary action here. */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CreditBalance balance={organisation.credits.balance} monthlyRemaining={organisation.credits.monthlyAllowance?.remaining} pending={organisation.credits.pending} available={organisation.credits.available} />
                  {canOrder && (
                    <Button variant="primary" size="sm" onClick={openBuyModal}>
                      <T _str="buy projects" swc />
                    </Button>
                  )}
                </div>
              </>
            )}
            {canManageCredits && organisation && (
              <>
                {/* The rest as one list, each opening a window of its own. */}
                <ActionList>
                  {canOrder && <ActionListItem icon={ClockIcon} title={<T _str="purchase history" swc />} description={<T _str="Your orders, with their invoices to download" />} onClick={openTransactionsModal} />}
                  {canOrder && <ActionListItem icon={ChatBubbleLeftRightIcon} title={<T _str="contact sales" swc />} description={<T _str="For a custom plan, or with any question" />} onClick={openContactSalesModal} />}
                  {canCancel && cancellableEndDate && (
                    <ActionListItem icon={XCircleIcon} title={<T _str="cancel subscription" swc />} description={<T _str="Stops the renewal. Your plan runs until the end of its term" />} onClick={openCancelModal} danger />
                  )}
                </ActionList>
                <TransactionsModal open={transactionsModalOpen} onClose={closeTransactionsModal} organisationId={organisation.id} />
                <BuyCreditsModal
                  open={buyModalOpen}
                  onClose={closeBuyModal}
                  organisationId={organisation.id}
                  projectUnitPrice={organisation.subscription.projectUnitPrice}
                  minAmount={organisation.subscription.minPurchaseAmount}
                  maxAmount={organisation.subscription.maxPurchaseAmount}
                />
                <ContactSalesModal open={contactSalesModalOpen} onClose={closeContactSalesModal} organisationId={organisation.id} />
                {canCancel && cancellableEndDate && <CancelSubscriptionModal open={cancelModalOpen} onClose={closeCancelModal} organisationId={organisation.id} endDate={cancellableEndDate} />}
              </>
            )}
          </div>
        )}
      </div>
    </PageContent>
  );
};
